import { Dispatch } from 'redux';
import axios from '../../axios';
import axiosAuth from '../../axiosAuth';
import { fireAnalytics } from '../../utils/firebase.js';
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts';
import { viewAccessRequest, loadUserData, activateFreeTrialAuth, setLoginAnimation, setLoginAnimationType } from '../actions/authActions.js';
import * as actionTypes from '../actions/types';
import * as actions from './actions.ts';
import { RegistrationV2Action } from './types.ts';
import { fetchSkillList } from '../../utils/onboardingV2/skillsService.ts';
import { resetOnboarding } from '../onboardingV2/actions.ts';
import { userHasJobboardRole } from '../../utils/jobboard/jobboardProfileGate.ts';

const performAutoLogin = async (email: string, password: string, dispatch: Dispatch<any>) => {
  try {
    const formData = {
      username: email,
      password: password,
    };

    const config = {
      headers: { Authorization: "Basic ZWRpZnk6bGFjbGF2ZXNlY3JldGExMjM0" },
    };

    const response = await axiosAuth.post("/oauth/token", formData, config);
    
    const { access_token, refresh_token, expires_in, name } = response.data;
    
    dispatch({
      type: actionTypes.SET_ACCESS,
      payload: {
        access: `Bearer ${access_token}`,
        refresh: `Bearer ${refresh_token}`,
        expire: expires_in,
        name: name,
      }
    });

    localStorage.setItem("access-Token-Edify", access_token);
    localStorage.setItem("access-RefreshToken-Edify", refresh_token);
    
    dispatch({ type: actionTypes.LOGIN_SUCCESS });
    dispatch(resetOnboarding());
    localStorage.removeItem('persist:onboardingV2');
    const getUserHeader = {
      headers: { Authorization: `Bearer ${access_token}` },
    };
    
    const userDataResponse = await axiosAuth.get("/user/data", getUserHeader);
    
    dispatch(viewAccessRequest());
    fireAnalytics("login_success", { 
      category: "authentication", 
      login_user_info: userDataResponse.data.id,
      edify_user_id: userDataResponse.data.id,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      login_method: "automatic",
      user_type: userDataResponse.data.type || "",
      action: "login_success"
    });
    dispatch({ type: actionTypes.GET_DATA, payload: userDataResponse.data });
    
    if (
      userDataResponse.data.free_trial?.status === "no-activated" &&
      !userHasJobboardRole(userDataResponse.data)
    ) {
      dispatch(activateFreeTrialAuth());
      dispatch(loadUserData());
    }
    
    return userDataResponse.data;
  } catch (error) {
    console.error('Error en auto login despu?s del registro:', error);
    throw error;
  }
};

export const createPreRegisterV2 = (email: string) => {
  return async (dispatch: Dispatch<RegistrationV2Action>) => {
    try {
      dispatch({ type: actions.REGISTRATION_V2_PRE_REGISTER_START });
      
      const response = await axios.post('/user/create/pre-register', { email });
      
      dispatch({
        type: actions.REGISTRATION_V2_PRE_REGISTER_SUCCESS,
        payload: response.data,
      });
      
      fireAnalytics("registro_email_enviado", { category: "usuario" });
      
      fireAnalytics("onboarding_email_sent", {
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "onboarding",
        action: "email_sent",
        email: email || ""
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error en createPreRegisterV2:', error);
      
      let errorMessage = "Ocurri? un error al enviar el c?digo de verificaci?n.";
      
      if (error?.response?.data?.message) {
        if (error.response.data.message === "El usuario ya est? registrado") {
          errorMessage = "Ya contamos con ese email registrado. Por favor, ve al login para acceder a tu cuenta.";
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        errorMessage = "Error de conexi?n. Verifica tu internet e intenta nuevamente.";
      }
      
      dispatch({
        type: actions.REGISTRATION_V2_PRE_REGISTER_FAIL,
        payload: errorMessage,
      });
      
      throw error;
    }
  };
};

export const validateVerificationCodeV2 = (email: string, code: string) => {
  return async (dispatch: Dispatch<RegistrationV2Action>) => {
    try {
      dispatch({ type: actions.REGISTRATION_V2_CODE_VALIDATION_START });
      
      const response = await axios.post('/user/create/validate-code', { email, code });
      
      dispatch({
        type: actions.REGISTRATION_V2_CODE_VALIDATION_SUCCESS,
        payload: response.data,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error en validateVerificationCodeV2:', error);
      
      let errorMessage = "Ocurri? un error al validar el c?digo.";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        errorMessage = "Error de conexi?n. Verifica tu internet e intenta nuevamente.";
      }
      
      dispatch({
        type: actions.REGISTRATION_V2_CODE_VALIDATION_FAIL,
        payload: errorMessage,
      });
      
      throw error;
    }
  };
};

export const resendVerificationCodeV2 = (email: string) => {
  return async (dispatch: Dispatch<RegistrationV2Action>) => {
    try {
      dispatch({ type: actions.REGISTRATION_V2_RESEND_CODE_START });
      
      const response = await axios.post('/user/create/resend-code', { email });
      
      dispatch({
        type: actions.REGISTRATION_V2_RESEND_CODE_SUCCESS,
        payload: response.data,
      });
      
      fireAnalytics("codigo_reenviado", { category: "usuario" });
      
      fireAnalytics("onboarding_code_resent", {
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "onboarding",
        action: "code_resent",
        email: email || ""
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error en resendVerificationCodeV2:', error);
      
      let errorMessage = "Ocurri? un error al reenviar el c?digo de verificaci?n.";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        errorMessage = "Error de conexi?n. Verifica tu internet e intenta nuevamente.";
      }
      
      dispatch({
        type: actions.REGISTRATION_V2_RESEND_CODE_FAIL,
        payload: errorMessage,
      });
      
      throw error;
    }
  };
};

export const completeUserRegistrationV2 = (email: string, password: string, skipOnboarding: boolean = false) => {
  return async (dispatch: Dispatch<RegistrationV2Action | any>) => {
    try {
      dispatch({ type: actions.REGISTRATION_V2_COMPLETE_REGISTRATION_START });
      dispatch(setLoginAnimationType('register'));
      dispatch(setLoginAnimation(true));
      
      localStorage.setItem("EmailRegister", email);
      
      const response = await axios.post('/user/create/register', {
        username: email,
        password: password
      });
     
      dispatch({
        type: actions.REGISTRATION_V2_COMPLETE_REGISTRATION_SUCCESS,
        payload: { data: response.data, skipOnboarding }
      });
      
      fireAnalytics("registro_completado_exitoso", { 
        category: "onboarding",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        action: "registration_completed"
      });
      
      try {
        const userData = await performAutoLogin(email, password, dispatch);
        
        fireAnalytics("login_automatico_despues_registro", { 
          category: "authentication",
          edify_user_id: userData?.id || "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          login_method: "automatic",
          action: "automatic_login_after_registration"
        });
        
        return { ...response.data, userData };
      } catch (loginError) {
        console.error('Error en login autom?tico:', loginError);
        fireAnalytics("login_automatico_fallido", { 
          category: "authentication",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          login_method: "automatic",
          action: "automatic_login_failed"
        });
        return response.data;
      }
      
    } catch (error: any) {
      console.error('Error en completeUserRegistrationV2:', error);
      
      let errorMessage = "Ocurri? un error al completar el registro.";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        errorMessage = "Error de conexi?n. Verifica tu internet e intenta nuevamente.";
      }
      
      dispatch({
        type: actions.REGISTRATION_V2_COMPLETE_REGISTRATION_FAIL,
        payload: errorMessage,
      });
      
      dispatch(setLoginAnimation(false));
      dispatch(setLoginAnimationType('login'));
      throw error;
    }
  };
};

export const loadSkillsForRegistration = () => {
  return async (dispatch: Dispatch<RegistrationV2Action>) => {
    try {
      const skills = await fetchSkillList();
      const datos = skills.map((s) => ({ id: s.id, name: s.name }));
      dispatch({
        type: actions.REGISTRATION_V2_SET_SKILLS,
        payload: datos,
      });
      return datos;
    } catch (error: any) {
      console.error('Error en loadSkillsForRegistration:', error);
      throw error;
    }
  };
};
